import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ApplyModal from "./ApplyModal";
import MultiStepFormModal from "./MultiStepFormModal";
import { FLOW_CONFIGS } from "./flowConfigs";
import {
  ComingSoonModal,
  SubmitStoryModal,
  ContactForm,
  NewsletterForm,
} from "./Modals";
import {
  Truck,
  Bike,
  Car,
  Anchor,
  Menu,
  X,
  ArrowRight,
  Play,
  Instagram,
  Youtube,
  Facebook,
  Mail,
  Sparkles,
  ChevronDown,
  Newspaper,
  Briefcase,
  Phone,
  PenSquare,
  Film,
  Users,
  Headphones,
  MessageCircle,
  Plus,
  Send,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_98369023-526e-447a-9536-c9b52a5bb5cf/artifacts/mnyeefvz_99DD8AA5-D993-42EB-AA6D-F95FD03FDFF8.png";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/75xsrqr8_copy_A7639DDB-9F7A-4FE8-B31C-791C89153126.jpeg";

const CULTURES = [
  {
    key: "trucking",
    label: "Highway Legends",
    Icon: Truck,
    img: "/cultures/highway-legends.png",
    blurb: "Long nights. Big roads. Real drivers keeping America moving.",
    route: "/trucking",
  },
  {
    key: "biking",
    label: "The Rideout",
    Icon: Bike,
    img: "/cultures/the-rideout.png",
    blurb: "More than bikes — it's loyalty, freedom, and the road between us.",
    route: "/motorcycles",
  },
  {
    key: "cars",
    label: "Street Machines",
    Icon: Car,
    img: "/cultures/street-machines.png",
    blurb: "Built in garages. Shown in the streets. Driven like art.",
    route: "/cars",
  },
  {
    key: "boating",
    label: "Lake Life",
    Icon: Anchor,
    img: "/cultures/lake-life.png",
    blurb: "Weekend takeovers, lake nights, and the lifestyle that floats different.",
    route: "/boating",
  },
];

const FALLBACK_SHOWS = [
  {
    id: "_fb1",
    tag: "unscripted",
    title: "Love in the Fast Lane",
    description:
      "Dating in the culture can be complicated — until now. A dating experience for bikers, truckers, car lovers, boaters, builders, and riders who live the lifestyle.",
    status: "now_casting",
    kind: "dating",
    cta_label: "Shoot Your Shot",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/tstxxcvb_63366FCD-3430-4F13-B40B-E09872280990.png",
  },
  {
    id: "_fb2",
    tag: "docuseries",
    title: "Built Not Bought",
    description:
      "Do you build, customize, or create machines that stand out in the culture?\n\nWe're looking for builders, garages, fabricators, and visionaries working on motorcycles, cars, trucks, and more.",
    status: "now_casting",
    kind: "built_not_bought",
    cta_label: "Submit Your Build",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/3v2xh8sr_copy_D0ADCA47-6C49-46C0-91CB-90516C8F432F.jpeg",
  },
  {
    id: "_fb3",
    tag: "docuseries",
    title: "Culture Files",
    description:
      "Everybody has a story… but not everybody tells it.\n\nWe're looking for real people with real experiences connected to the culture — moments that changed everything, stories that need to be heard, and perspectives that haven't been seen.",
    status: "now_casting",
    kind: "culture_files",
    cta_label: "Submit Your Story",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/rry9s4g2_copy_38C57C12-14AB-42C0-9C65-BB7D859309F5.jpeg",
  },
  {
    id: "_fb4",
    tag: "event series",
    title: "We Outside",
    description:
      "If it's lit… we pull up.\n\nAutoMotoVille is hitting the road to find the most electrifying events in the culture — from MC annuals and car meets to truck shows, boat parties, ride-outs, and street takeovers.",
    status: "now_casting",
    kind: "events",
    cta_label: "Submit Your Event to We Outside",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/d2395beg_A5884D80-C4CF-4EC1-9EEB-ACBDA8777EA8.png",
  },
  {
    id: "_fb5",
    tag: "feature",
    title: "Reenactment Talent",
    description:
      "Want to be on camera and bring real stories to life?\n\nWe're casting actors, riders, drivers, builders, and real personalities to help recreate powerful moments across AutoMotoVille shows. No experience required — but presence matters.",
    status: "now_casting",
    kind: "reenactment",
    cta_label: "Join Reenactment Casting",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/iox4fxnq_copy_6499631C-6D83-46D5-A4E7-EA195691B7E5.jpeg",
  },
  {
    id: "_fb6",
    tag: "competition",
    title: "Street Eats & Engines",
    description:
      "Do you THINK that you have one of the best food trucks or catering setups in the culture?\n\nWe're looking for food vendors who bring real flavor to the culture. Submit your business for a chance to be featured on AutoMotoVille — a panel will pull up and judge if the food hits like you say it does.\n\nIf it's hitting… the culture will know.",
    status: "now_casting",
    kind: "food",
    cta_label: "Submit Your Food Business",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/tx22cspx_IMG_8590.png",
  },
  {
    id: "_fb7",
    tag: "performance",
    title: "Stage for the Streets",
    description:
      "The streets decide who's next.\n\nRaw talent meets real opportunity — no gatekeeping, no politics. Selected performers hit the stage, the crowd decides, and top talent gets booked.",
    status: "now_casting",
    kind: "performers",
    cta_label: "Submit Your Performance",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/xubdvo1j_117F3023-0ADA-49D2-869F-0F10D3E4BDF0.png",
  },
  {
    id: "_fb8",
    tag: "originals",
    title: "Independent Creators & Filmmakers",
    description:
      "Bring your COMPLETED content. We'll bring it to the culture.\n\nWe're accepting fully completed content from independent creators. If the audience connects, it wins — and when it wins, you earn.",
    status: "now_casting",
    kind: "content-creators",
    cta_label: "Submit Your Project For Approval",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/e955el2o_28F03F70-8EC4-4458-9589-B440F423EC78.png",
  },
  {
    id: "_fb9",
    tag: "documentary",
    title: "The Set Speaks",
    description:
      "Now casting MC & SC club members.\n\nEvery patch has a perspective. The motorcycle set has changed — some say the culture evolved, others say the code was lost. Now the people who lived it are finally speaking.",
    status: "now_casting",
    kind: "set-speaks",
    cta_label: "Apply Now",
    external_url: "/the-set-speaks",
    image_url:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/fsq1ep9o_102609F8-756D-4BDE-920D-A04F846FDB7E.png",
  },
  {
    id: "_fb10",
    tag: "reality",
    title: "Booked & Outside",
    description:
      "The culture travels different.\n\nA culture-driven travel reality series following aspiring travel agents, creators, entrepreneurs, bikers, truckers, couples, influencers, and personalities as they learn the travel business, build their brands, and travel the world.",
    status: "now_casting",
    kind: "booked-outside",
    cta_label: "Apply Now",
    external_url: "/booked-outside",
    image_url: "/booked-outside-hero.png",
  },
];

function Header({ nav: navHandlers }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { label: "Now Casting", action: () => navHandlers.scrollTo("shows") },
    { label: "Shows", action: () => navHandlers.scrollTo("shows") },
    { label: "Culture", action: () => navHandlers.scrollTo("culture") },
    { label: "About", action: () => navHandlers.scrollTo("about") },
  ];

  const handleClick = (action) => {
    action();
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-8 py-3 md:py-4">
        {/* Logo - top-left (scrolls to top) */}
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
          aria-label="AutoMotoVille — back to top"
        >
          <img
            src={LOGO}
            alt="AutoMotoVille"
            data-testid="header-logo"
            style={{ height: "56px", width: "auto" }}
            className="amv-logo drop-shadow-[0_0_12px_rgba(0,229,255,0.35)] group-hover:drop-shadow-[0_0_22px_rgba(255,45,156,0.55)] transition-all duration-300"
          />
        </button>

        {/* Center nav (desktop) */}
        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((n) => (
            <button
              key={n.label}
              type="button"
              onClick={() => handleClick(n.action)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className="font-accent text-sm text-white/80 hover:text-[#00E5FF] transition-colors whitespace-nowrap"
            >
              {n.label}
            </button>
          ))}
        </nav>

        {/* Sign in - top right */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => handleClick(navHandlers.openSignIn)}
            data-testid="signin-link"
            className="hidden md:inline-flex font-accent text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap"
          >
            Sign In
          </button>
          <button
            onClick={() => handleClick(navHandlers.openApply)}
            data-testid="header-apply-btn"
            className="hidden md:inline-flex items-center gap-2 bg-[#FF2D9C] text-white font-bold px-4 py-2 uppercase tracking-widest text-xs glow-pink transition-all whitespace-nowrap"
          >
            Apply <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            className="md:hidden text-white"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {navItems.map((n) => (
            <button
              key={n.label}
              type="button"
              onClick={() => handleClick(n.action)}
              className="text-left font-accent text-lg text-white/90"
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
            >
              {n.label}
            </button>
          ))}
          <div className="h-px bg-white/10 my-1" />
          <button
            type="button"
            onClick={() => handleClick(navHandlers.openApply)}
            data-testid="mobile-apply-link"
            className="text-left font-accent text-base text-[#FF2D9C]"
          >
            Apply Now
          </button>
          <button
            type="button"
            onClick={() => handleClick(navHandlers.openStory)}
            data-testid="mobile-submit-story-link"
            className="text-left font-accent text-base text-white/85"
          >
            Submit a Story
          </button>
          <button
            type="button"
            onClick={() => handleClick(() => navHandlers.scrollTo("contact"))}
            data-testid="mobile-contact-link"
            className="text-left font-accent text-base text-white/85"
          >
            Contact
          </button>
          <button
            type="button"
            onClick={() => handleClick(navHandlers.openCareers)}
            data-testid="mobile-careers-link"
            className="text-left font-accent text-base text-white/85"
          >
            Careers
          </button>
          <button
            type="button"
            onClick={() => handleClick(navHandlers.openSignIn)}
            className="text-left font-accent text-sm text-white/60"
            data-testid="mobile-signin-link"
          >
            Sign In
          </button>
        </div>
      )}
    </header>
  );
}

function Hero({ onApply, onCasting }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative md:min-h-[100svh] overflow-hidden"
    >
      {/* Mobile bg fill — solid dark + subtle neon glow so the stacked
          text/image read against a clean cinematic ground. */}
      <div
        className="absolute inset-0 md:hidden bg-[#05050a]"
        style={{
          backgroundImage:
            "radial-gradient(60% 50% at 22% 30%, rgba(255,45,156,0.10), transparent 60%), radial-gradient(65% 55% at 82% 70%, rgba(0,229,255,0.14), transparent 66%)",
        }}
        aria-hidden
      />
      {/* Cinematic hero background — DESKTOP/TABLET-md+ only. On mobile
          the image renders in-flow below the text (no overlap). */}
      <div className="absolute inset-0 hero-bg-cinematic hidden md:block">
        {/* Blurred backdrop fill */}
        <div
          className="absolute inset-0 bg-no-repeat bg-center scale-110 blur-2xl opacity-70 saturate-125"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
          }}
          aria-hidden
        />
        {/* Foreground — full image, contained, centered */}
        <img
          src={HERO_BG}
          alt="AutoMotoVille — Real Culture. Real Stories. Real Entertainment."
          className="absolute inset-0 w-full h-full object-contain object-center select-none pointer-events-none [filter:saturate(1.10)_contrast(1.06)_brightness(1.04)] md:[filter:saturate(1.08)_contrast(1.04)_brightness(1.01)]"
          draggable={false}
          data-testid="hero-bg-image"
        />
        {/* Soft blue/cyan + pink glow wash — subtle, doesn't cover the subject. */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen opacity-[0.18]"
          style={{
            background:
              "radial-gradient(60% 50% at 22% 40%, rgba(255,45,156,0.35), transparent 60%), radial-gradient(65% 55% at 82% 55%, rgba(0,229,255,0.55), transparent 66%)",
          }}
        />
        {/* Gentle cool-blue ambient tint. */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-soft-light opacity-[0.18]"
          style={{
            background:
              "linear-gradient(115deg, rgba(0,120,200,0.40) 0%, transparent 55%)",
          }}
        />
        {/* Top + bottom dark gradient — top stays light for the AMV logo
            in the image to read, bottom is stronger so the headline + CTAs
            sit cleanly over a dark band (not over the subjects). */}
        <div
          className="absolute inset-0 pointer-events-none md:hidden"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,10,0.45) 0%, rgba(5,5,10,0.05) 14%, rgba(5,5,10,0) 28%, rgba(5,5,10,0) 52%, rgba(5,5,10,0.55) 66%, rgba(5,5,10,0.86) 82%, rgba(5,5,10,0.97) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,10,0.62) 0%, rgba(5,5,10,0.12) 14%, rgba(5,5,10,0) 30%, rgba(5,5,10,0) 55%, rgba(5,5,10,0.35) 70%, rgba(5,5,10,0.75) 88%, rgba(5,5,10,0.96) 100%)",
          }}
        />
        {/* Soft vignette — cinematic edge fade. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 60%, rgba(5,5,10,0.55) 100%)",
          }}
        />
        {/* Subtle scanlines for TV-promo feel — softer on mobile */}
        <div className="absolute inset-0 scanlines opacity-20 md:opacity-30 pointer-events-none" />
        {/* Grain */}
        <div className="absolute inset-0 grain-overlay pointer-events-none" />
      </div>

      {/* Content layer — three-zone layout: top eyebrow / mid spacer (hero subject breathes) / bottom text + CTA */}
      <div className="relative z-10 md:min-h-[100svh] max-w-7xl mx-auto px-6 pt-24 md:pt-32 pb-12 flex flex-col items-center text-center">
        {/* TOP: Eyebrow only — primary logo lives in the header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#00E5FF]" />
          <span
            className="font-accent text-xs md:text-sm text-white drop-shadow-[0_0_10px_rgba(0,229,255,0.55)]"
            data-testid="hero-eyebrow"
          >
            Where the Culture Lives
          </span>
          <span className="h-px w-10 bg-[#FF2D9C]" />
        </motion.div>

        {/* Spacer — DESKTOP only. On mobile, the text sits tight under
            the eyebrow and the image renders in-flow below the subhead. */}
        <div className="hidden md:block flex-[1.15] md:min-h-[36vh]" />

        {/* BOTTOM: Tagline + CTAs */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-5 md:mt-0 font-heading text-2xl sm:text-3xl md:text-6xl font-black leading-[1.06] md:leading-[1.02] max-w-xs sm:max-w-2xl md:max-w-4xl drop-shadow-[0_6px_30px_rgba(0,0,0,0.9)]"
          data-testid="hero-headline"
        >
          <span className="text-[#FF2D9C]">Real</span> Culture.{" "}
          <span className="neon-text">Real Stories.</span>{" "}
          Real Entertainment.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-3 md:mt-5 text-xs sm:text-sm md:text-lg text-white/85 max-w-xs sm:max-w-xl md:max-w-2xl leading-relaxed drop-shadow-[0_4px_18px_rgba(0,0,0,0.9)]"
          data-testid="hero-subcopy"
        >
          The next generation of scripted &amp; unscripted television
          celebrating trucking, biking, cars, and boating culture.
        </motion.p>

        {/* MOBILE-ONLY in-flow hero image — sits BELOW the text so nothing
            overlaps. Desktop continues to use the absolute-positioned
            background image above. */}
        <motion.img
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          src={HERO_BG}
          alt="AutoMotoVille — Boss Lady, the crew, and the rides."
          className="md:hidden mt-6 w-full max-w-md select-none pointer-events-none rounded-sm shadow-[0_20px_60px_-20px_rgba(255,45,156,0.45),0_20px_60px_-30px_rgba(0,229,255,0.4)] [filter:saturate(1.08)_contrast(1.04)_brightness(1.04)]"
          draggable={false}
          data-testid="hero-mobile-image"
        />

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-5"
        >
          <button
            onClick={onApply}
            data-testid="hero-apply-now-btn"
            className="relative group bg-[#FF2D9C] text-white font-black px-10 md:px-14 py-4 md:py-5 uppercase tracking-[0.25em] text-sm md:text-lg glow-pink transition-all hover:scale-[1.03]"
          >
            <span className="relative z-10 flex items-center gap-3">
              <Sparkles className="w-5 h-5" /> Apply Now
            </span>
            <span className="absolute inset-0 border border-white/20 pointer-events-none" />
          </button>

          <button
            onClick={onCasting}
            data-testid="hero-now-casting-btn"
            className="bg-black/30 backdrop-blur-sm text-[#00E5FF] font-bold px-8 md:px-10 py-4 md:py-5 uppercase tracking-[0.25em] text-xs md:text-base border border-[#00E5FF]/60 glow-cyan transition-all"
          >
            <span className="flex items-center gap-2">
              <Play className="w-4 h-4 fill-[#00E5FF]" /> Now Casting
            </span>
          </button>
        </motion.div>

        {/* bottom spacer to push text slightly above bottom */}
        <div className="flex-[0.35] min-h-[4vh]" />
      </div>
    </section>
  );
}

function Marquee() {
  const words = [
    "TRUCKING",
    "BIKING",
    "CARS",
    "BOATING",
    "GARAGES",
    "HIGHWAYS",
    "HARBORS",
    "HORSEPOWER",
  ];
  const row = [...words, ...words, ...words];
  return (
    <div
      className="relative overflow-hidden border-y border-white/10 bg-black py-6"
      data-testid="marquee-strip"
    >
      <div className="marquee-track flex gap-16 whitespace-nowrap">
        {row.map((w, i) => (
          <span
            key={i}
            className={`font-accent text-4xl md:text-6xl ${
              i % 2 === 0 ? "text-white" : "text-[#FF2D9C]"
            }`}
          >
            {w}
            <span className="text-[#00E5FF] mx-8">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Culture() {
  const navigate = useNavigate();
  return (
    <section
      id="culture"
      data-testid="culture-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <div className="font-accent text-sm text-[#FF2D9C] mb-4">
              / 01 — THE CULTURES
            </div>
            <h2 className="font-heading text-4xl md:text-6xl font-black leading-[1.02] max-w-2xl">
              Four worlds.
              <br />
              <span className="neon-text">One network.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md leading-relaxed">
            AutoMotoVille isn&rsquo;t polished reality TV. It&rsquo;s the grease
            under fingernails, the salt in the air, the 2am pit stop. We film
            the people the algorithm forgot.
          </p>
        </div>

        {/* Asymmetric bento */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
          {CULTURES.map((c, idx) => {
            // Bento sizing — cards 02 + 03 (the two on the right column)
            // each get ~310px instead of 250px to give the rideout + street
            // machines more breathing room. Card 01 grows from 520→640 so
            // it still equals 2× the right-column cards (310+20gap+310 = 640).
            const span =
              idx === 0
                ? "md:col-span-4 md:row-span-2 md:h-[640px]"
                : idx === 1
                  ? "md:col-span-2 md:h-[310px]"
                  : idx === 2
                    ? "md:col-span-2 md:h-[310px]"
                    : "md:col-span-6 md:h-[420px]";
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.08 }}
                data-testid={`culture-card-${c.key}`}
                role="link"
                tabIndex={0}
                aria-label={`${c.label} — open culture page`}
                onClick={() => navigate(c.route)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(c.route);
                  }
                }}
                className={`relative group overflow-hidden border border-white/10 h-[320px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/70 ${span} hover:border-[#FF2D9C]/60 transition-all duration-500 hover:shadow-[0_20px_60px_-20px_rgba(255,45,156,0.35),0_0_0_1px_rgba(0,229,255,0.18)_inset]`}
              >
                {/* Cinematic image — gentle zoom on hover */}
                <div
                  className="absolute inset-0 bg-cover bg-center scale-[1.04] group-hover:scale-[1.10] transition-transform duration-[1.6s] ease-out"
                  style={{ backgroundImage: `url(${c.img})` }}
                />
                {/* Bottom-up dark gradient for title legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
                {/* Side neon vignette — pink left, cyan right — feels filmic */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(255,45,156,0.18) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 65%, rgba(0,229,255,0.16) 100%)",
                  }}
                />
                {/* Corner index + icon */}
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-9 h-9 border border-white/25 backdrop-blur-sm bg-black/30 flex items-center justify-center">
                        <c.Icon className="w-4 h-4 text-[#00E5FF]" />
                      </span>
                      <span className="font-accent text-[10px] tracking-[0.32em] text-white/85">
                        / CATEGORY {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                  <div>
                    <h3
                      className="font-heading uppercase font-black leading-[0.95] tracking-tight text-3xl md:text-5xl"
                      style={{
                        color: "#F6EBD4",
                        textShadow:
                          "0 2px 18px rgba(0,0,0,0.55), 0 0 1px rgba(255,255,255,0.5)",
                      }}
                    >
                      {c.label}
                    </h3>
                    <p className="text-[13px] md:text-sm text-white/75 mt-3 max-w-md leading-relaxed">
                      {c.blurb}
                    </p>
                  </div>
                </div>
                {/* Left edge accent on hover */}
                <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-[#FF2D9C] via-white/0 to-[#00E5FF] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Featured({ onApply, posterImage }) {
  const hasImage = !!posterImage;
  return (
    <section
      id="featured"
      data-testid="featured-section"
      className="relative py-24 md:py-28 px-6 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#05050a] via-[#0d0d14] to-[#05050a]" />
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 20%, rgba(200,162,74,0.20), transparent 42%), radial-gradient(circle at 88% 78%, rgba(255,45,156,0.18), transparent 46%)",
        }}
      />
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="font-accent text-sm text-[#C8A24A] mb-4 tracking-[0.22em]">
          / FEATURED — AUTOMOTOVILLE ORIGINAL
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative border border-white/10 bg-[#0a0a12]/70 backdrop-blur-sm p-8 sm:p-12 md:p-16 overflow-hidden"
          data-testid="featured-set-speaks"
        >
          <div
            className="absolute -top-24 -right-24 w-[340px] h-[340px] rounded-full opacity-25 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(200,162,74,0.55), transparent 65%)",
            }}
          />
          <div
            className="absolute -bottom-24 -left-24 w-[320px] h-[320px] rounded-full opacity-25 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(255,45,156,0.45), transparent 65%)",
            }}
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#C8A24A]/55 text-[#C8A24A] font-accent text-[11px] tracking-[0.22em] mb-5">
                <Film className="w-3.5 h-3.5" /> FLAGSHIP DOCUSERIES
              </div>
              <div className="font-accent text-[11px] sm:text-xs tracking-[0.32em] text-[#C8A24A]/85 mb-3 uppercase">
                The Set Speaks
              </div>
              <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black leading-[1.02]">
                Every voice. Every story.
                <br />
                <span className="neon-text">One culture.</span>
              </h2>
              <p className="mt-6 text-white/75 leading-relaxed max-w-xl text-base sm:text-lg">
                MC &amp; SC. Old school. New school. One conversation. Real
                voices from the motorcycle set.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={onApply}
                  data-testid="featured-cta-apply"
                  className="inline-flex items-center justify-center gap-2 bg-[#C8A24A] text-black font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:scale-[1.03] transition-transform"
                >
                  <Sparkles className="w-4 h-4" /> Apply Now
                </button>
              </div>
            </div>

            {hasImage ? (
              <div
                className="lg:col-span-2 relative aspect-[3/4] overflow-hidden border border-[#C8A24A]/25 bg-[#0a0a12]"
                data-testid="featured-set-speaks-poster"
              >
                {/* Blurred backdrop — same image, scaled + blurred + dim */}
                <div
                  className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-70 saturate-150"
                  style={{ backgroundImage: `url(${posterImage})` }}
                  aria-hidden
                />
                <div className="absolute inset-0 bg-black/30" aria-hidden />
                {/* Foreground poster — full artwork, top-aligned */}
                <img
                  src={posterImage}
                  alt="The Set Speaks key art"
                  className="relative w-full h-full object-contain object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-[#C8A24A]/25 pointer-events-none" />
              </div>
            ) : (
              <div className="lg:col-span-2 space-y-3">
                {[
                  "Every patch has a perspective",
                  "Real OG voices on camera",
                  "Documentary, not reality TV",
                  "Now casting MC + SC members",
                ].map((t, i) => (
                  <motion.div
                    key={t}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.08 }}
                    className="flex items-center gap-3 border border-white/10 bg-black/30 px-4 py-3"
                  >
                    <span className="w-2 h-2 bg-[#C8A24A] rounded-full shrink-0" />
                    <span className="font-accent text-xs tracking-widest text-white/80">
                      {t.toUpperCase()}
                    </span>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Category-driven tiles. Each tile maps directly to one show + one intake action.
// `showKind` is matched against the live `shows` data from the API to pick the
// correct thumbnail. `action` is the routing key used by handlePickLane.
const LANE_TILES = [
  {
    key: "dating",
    category: "Dating",
    showName: "Love in the Fast Lane",
    hook: "A dating experience built for the culture.",
    showKind: "dating",
    action: "love-in-the-fast-lane",
    accent: "#FF2D9C",
    cta: "Shoot Your Shot",
  },
  {
    key: "builds",
    category: "Builds",
    showName: "Built Not Bought",
    hook: "Builders, garages, fabricators — show us the work.",
    showKind: "built_not_bought",
    action: "built-not-bought",
    accent: "#00E5FF",
  },
  {
    key: "stories",
    category: "Stories",
    showName: "Culture Files",
    hook: "Real people. Real stories. Cinematically told.",
    showKind: "culture_files",
    action: "culture-files",
    accent: "#FF2D9C",
  },
  {
    key: "events",
    category: "Events",
    showName: "We Outside",
    hook: "If it's lit… we pull up.",
    showKind: "events",
    action: "events",
    accent: "#00E5FF",
  },
  {
    key: "food",
    category: "Food",
    showName: "Street Eats & Engines",
    hook: "If your food hits, the culture will know.",
    showKind: "food",
    action: "food",
    accent: "#FF2D9C",
  },
  {
    key: "performers",
    category: "Performers",
    showName: "Stage for the Streets",
    hook: "The streets decide who's next.",
    showKind: "performers",
    action: "performers",
    accent: "#00E5FF",
    cta: "Submit Your Performance",
  },
  {
    key: "talent",
    category: "Talent",
    showName: "Reenactment Talent",
    hook: "We're casting actors, riders, and personalities to reenact real moments from the culture.",
    showKind: "reenactment",
    action: "reenactment",
    accent: "#FF2D9C",
    cta: "Join Reenactment Casting",
  },
  {
    key: "creators",
    category: "Creators",
    showName: "Independent Creators & Filmmakers",
    hook: "Bring your completed content. We'll bring it to the culture.",
    showKind: "content-creators",
    action: "content-creators",
    accent: "#00E5FF",
    cta: "Submit For Approval",
  },
  {
    key: "set-speaks",
    category: "Documentary",
    showName: "The Set Speaks",
    hook: "MC & SC. Old school. New school. One conversation. Real voices from the motorcycle set.",
    showKind: "set-speaks",
    action: "set-speaks-page",
    accent: "#C8A24A",
    cta: "Apply Now",
  },
  {
    key: "booked-outside",
    category: "Reality / Travel",
    showName: "Booked & Outside",
    hook: "The culture travels different. Travel agents, creators, entrepreneurs, bikers, truckers, couples — building brands while seeing the world.",
    showKind: "booked-outside",
    action: "booked-outside-page",
    accent: "#FF85C0",
    cta: "Apply Now",
  },
];

function LanePicker({ onPick, shows }) {
  const showByKind = (k) => shows.find((s) => (s.kind || "") === k);

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
      data-testid="lane-picker"
    >
      {LANE_TILES.map((tile, i) => {
        const show = showByKind(tile.showKind);
        const img = show?.image_url || "";
        return (
          <motion.button
            key={tile.key}
            type="button"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.55, delay: (i % 4) * 0.06 }}
            onClick={() => onPick && onPick(tile.action)}
            data-testid={`lane-tile-${tile.key}`}
            className="group relative text-left flex flex-col border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
            style={{ "--accent": tile.accent }}
          >
            {/* Top accent line on hover */}
            <div
              className="absolute -top-px left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500 z-10"
              style={{ backgroundColor: tile.accent }}
            />

            {/* Thumbnail — uses bg-contain so the FULL poster artwork is
                always visible (no cropped titles, faces or logos). The
                surrounding letterbox area is filled by a blurred, low-opacity
                copy of the same poster so the empty space feels intentional
                and cinematic instead of dead. */}
            <div className="relative aspect-[3/4] overflow-hidden bg-[#0a0a12]">
              {img ? (
                <>
                  {/* Blurred backdrop — same image, scaled + blurred + dim */}
                  <div
                    className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-70 saturate-150"
                    style={{ backgroundImage: `url(${img})` }}
                    aria-hidden
                  />
                  {/* Dark wash so foreground poster pops */}
                  <div className="absolute inset-0 bg-black/30" aria-hidden />
                  {/* Foreground poster — full artwork, top-aligned */}
                  <div
                    className="absolute inset-0 bg-contain bg-top bg-no-repeat"
                    style={{ backgroundImage: `url(${img})` }}
                  />
                </>
              ) : (
                <>
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `radial-gradient(circle at 25% 30%, ${tile.accent}55, transparent 60%), radial-gradient(circle at 80% 70%, ${tile.accent}33, transparent 65%), #0a0a12`,
                    }}
                  />
                  <div
                    className="absolute inset-0 opacity-[0.07] pointer-events-none"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(0deg, rgba(255,255,255,0.8) 0, rgba(255,255,255,0.8) 1px, transparent 1px, transparent 4px)",
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={LOGO}
                      alt=""
                      className="w-2/3 max-w-[160px] opacity-30 group-hover:opacity-50 transition-opacity duration-500 amv-logo"
                    />
                  </div>
                </>
              )}
              {/* Bottom fade */}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0d0d14] to-transparent pointer-events-none" />
              {/* Category chip floating on poster */}
              <div className="absolute top-3 left-3">
                <span
                  className="font-accent text-[10px] tracking-[0.22em] px-2 py-1 bg-black/65 backdrop-blur-md border"
                  style={{
                    color: tile.accent,
                    borderColor: `${tile.accent}66`,
                  }}
                >
                  {tile.category.toUpperCase()}
                </span>
              </div>
              <div className="absolute top-3 right-3">
                <span className="font-accent text-[10px] text-white/85 px-2 py-1 bg-black/60 backdrop-blur-md border border-white/15">
                  /{String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="relative p-5 md:p-6 flex flex-col flex-1">
              <h3 className="font-heading text-xl md:text-2xl font-black leading-tight mb-1.5 text-white">
                {tile.showName}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-5 flex-1">
                {tile.hook}
              </p>
              <span
                className={`self-stretch sm:self-start text-center sm:text-left inline-flex items-center justify-center sm:justify-start gap-2 font-accent text-[11px] tracking-[0.22em] px-4 py-2.5 transition-all group-hover:gap-3 ${
                  tile.accent === "#FF2D9C" ? "glow-pink" : "glow-cyan"
                }`}
                style={{
                  backgroundColor: tile.accent,
                  color: "#0a0a12",
                  fontWeight: 800,
                }}
              >
                {(tile.cta || "Submit Now").toUpperCase()}{" "}
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}

function Shows({ onPickLane, shows: showsProp }) {
  const [localShows, setLocalShows] = useState(FALLBACK_SHOWS);

  useEffect(() => {
    if (showsProp) return; // parent supplies data
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shows`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        if (active && Array.isArray(data) && data.length > 0) setLocalShows(data);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      active = false;
    };
  }, [showsProp]);

  const shows = showsProp && showsProp.length > 0 ? showsProp : localShows;

  return (
    <section
      id="shows"
      data-testid="shows-section"
      className="relative py-28 md:py-36 px-6 bg-[#0d0d14]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-14 max-w-4xl">
          <div className="font-accent text-sm text-[#00E5FF] mb-4 tracking-[0.22em]">
            / 02 — NOW CASTING
          </div>
          <h2 className="font-heading text-4xl md:text-6xl font-black leading-[1.02]">
            Not just casting…
            <br />
            <span className="neon-text">we're building the culture.</span>
          </h2>
          <div className="mt-7 space-y-4 text-white/70 leading-relaxed text-base md:text-lg">
            <p>
              AutoMotoVille is looking for real people, real talent, real
              businesses, events, and creators across biking, cars, trucking,
              boating, and lifestyle culture.
            </p>
            <p className="text-white/85 font-accent text-sm tracking-[0.18em]">
              SELECT YOUR LANE BELOW AND SUBMIT YOUR INFORMATION.
            </p>
          </div>
        </div>

        <LanePicker onPick={onPickLane} shows={shows} />
      </div>
    </section>
  );
}

function StartScreen({ onApply }) {
  return (
    <section
      id="casting"
      data-testid="start-screen-section"
      className="relative py-32 md:py-44 px-6 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#05050a] via-[#0d0d14] to-[#05050a]" />
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(255,45,156,0.35), transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,229,255,0.3), transparent 45%)",
        }}
      />
      <div className="relative z-10 max-w-5xl mx-auto text-center">
        <div className="font-accent text-sm text-[#FF2D9C] mb-6">
          / 03 — APPLICATION START
        </div>

        {/* Start-screen logo - third instance */}
        <div className="inline-flex items-center justify-center p-4 rounded-full pulse-ring mb-10">
          <img
            src={LOGO}
            alt="AutoMotoVille"
            data-testid="start-screen-logo"
            className="amv-logo w-[min(420px,80vw)] drop-shadow-[0_0_40px_rgba(0,229,255,0.45)]"
          />
        </div>

        <h2
          className="font-heading text-4xl md:text-6xl font-black max-w-3xl mx-auto leading-[1.02]"
          data-testid="start-headline"
        >
          Your story <span className="neon-text">deserves the screen.</span>
        </h2>
        <p className="mt-6 text-white/70 max-w-2xl mx-auto leading-relaxed">
          We&rsquo;re casting drivers, riders, builders, captains, and the
          families behind them. No resumes required — just your garage, your
          rig, your life.
        </p>

        {/* CTAs */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5">
          <button
            onClick={onApply}
            data-testid="start-apply-now-btn"
            className="relative bg-[#FF2D9C] text-white font-black px-14 py-6 uppercase tracking-[0.28em] text-base md:text-lg glow-pink transition-all hover:scale-[1.04]"
          >
            <span className="flex items-center gap-3">
              <Sparkles className="w-5 h-5" /> Apply Now
            </span>
          </button>
          <button
            data-testid="start-now-casting-btn"
            className="bg-transparent text-[#00E5FF] font-bold px-10 py-5 uppercase tracking-[0.28em] text-sm md:text-base border border-[#00E5FF]/60 glow-cyan transition-all"
          >
            <Play className="w-4 h-4 fill-[#00E5FF] inline mr-2" /> Now Casting
          </button>
        </div>

        {/* mini checklist */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {[
            "Fast 3-step form",
            "No audition tape required",
            "Producer response in 14 days",
          ].map((t) => (
            <div
              key={t}
              className="border border-white/10 px-5 py-4 bg-white/[0.02] text-left flex items-center gap-3"
            >
              <span className="w-2 h-2 bg-[#00E5FF] rounded-full" />
              <span className="text-sm text-white/75">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  const [activePillar, setActivePillar] = useState(null);
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <div className="font-accent text-sm text-[#00E5FF] mb-4">
            / 04 — THE NETWORK
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black leading-[1.05] mb-6">
            Built by the <span className="neon-text">culture</span>, for the
            culture.
          </h2>
          <p className="text-white/70 leading-relaxed mb-6">
            AutoMotoVille is a television network devoted to the people behind
            the engines. We partner with independent creators, working
            mechanics, night-shift drivers, and lifers who know their machines
            by sound.
          </p>
          <p className="text-white/60 leading-relaxed">
            No reality-TV artifice. No parachuted-in hosts. Just authentic
            American gearhead culture — told by the people living it.
          </p>
        </div>
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              k: "Independent",
              v: "Producer-owned",
              accent: "#FF2D9C",
              body: "Producer-owned. Culture-driven. No outside gatekeepers. AutoMotoVille is built by people living the lifestyle.",
            },
            {
              k: "Authentic",
              v: "Real people, real rigs",
              accent: "#00E5FF",
              body: "Real riders. Real drivers. Real builders. No scripted personalities or parachuted hosts.",
            },
            {
              k: "Distributed",
              v: "Streaming + linear 2026",
              accent: "#C8A24A",
              body: "Streaming + future linear distribution. Designed for documentaries, reality series, podcasts, and live culture events.",
            },
            {
              k: "Open Casting",
              v: "Year-round submissions",
              accent: "#FF2D9C",
              body: "Year-round submissions for riders, creators, performers, storytellers, clubs, builders, truckers, and culture personalities.",
            },
          ].map((x) => (
            <button
              key={x.k}
              type="button"
              onClick={() => setActivePillar(x)}
              data-testid={`pillar-card-${x.k.toLowerCase().replace(/\s+/g, "-")}`}
              aria-label={`Open ${x.k} details`}
              className="group relative text-left border border-white/10 p-6 bg-white/[0.02] hover:bg-white/[0.04] transition-all overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/60"
              style={{ borderColor: undefined }}
            >
              <span
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  boxShadow: `0 12px 36px -14px ${x.accent}55, inset 0 0 0 1px ${x.accent}66`,
                }}
                aria-hidden
              />
              <span
                className="absolute top-0 left-0 h-full w-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: x.accent }}
                aria-hidden
              />
              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-2xl font-black text-white group-hover:text-white transition-colors">
                    {x.k}
                  </div>
                  <div
                    className="font-accent text-xs mt-2 tracking-widest"
                    style={{ color: `${x.accent}cc` }}
                  >
                    {x.v.toUpperCase()}
                  </div>
                </div>
                <Plus
                  className="shrink-0 w-4 h-4 text-white/40 group-hover:text-white group-hover:rotate-90 transition-all duration-300"
                  aria-hidden
                />
              </div>
            </button>
          ))}
        </div>
      </div>

      <PillarDetailModal
        pillar={activePillar}
        onClose={() => setActivePillar(null)}
      />
    </section>
  );
}

function PillarDetailModal({ pillar, onClose }) {
  return (
    <AnimatePresence>
      {pillar && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center px-4 pb-4 sm:p-6"
          data-testid="pillar-modal"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="pillar-modal-title"
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0c0a10]/95 backdrop-blur-xl border border-white/10 overflow-hidden"
            style={{
              boxShadow: `0 30px 80px -20px ${pillar.accent}66, inset 0 0 0 1px ${pillar.accent}33`,
            }}
          >
            <span
              className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-40 pointer-events-none"
              style={{
                background: `radial-gradient(circle, ${pillar.accent}88, transparent 65%)`,
              }}
              aria-hidden
            />
            <span
              className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-30 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,229,255,0.6), transparent 65%)",
              }}
              aria-hidden
            />

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="pillar-modal-close"
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative p-7 sm:p-9">
              <div
                className="font-accent text-[10px] tracking-[0.32em] mb-3"
                style={{ color: pillar.accent }}
              >
                / AUTOMOTOVILLE NETWORK PRINCIPLE
              </div>
              <h3
                id="pillar-modal-title"
                className="font-heading uppercase font-black text-3xl sm:text-4xl leading-[0.95] tracking-tight"
                style={{ color: "#F6EBD4" }}
              >
                {pillar.k}
              </h3>
              <div
                className="mt-2 font-accent text-[11px] tracking-[0.22em] uppercase"
                style={{ color: `${pillar.accent}cc` }}
              >
                {pillar.v}
              </div>
              <p className="mt-6 text-white/80 leading-relaxed text-[15px]">
                {pillar.body}
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="pillar-modal-dismiss"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 font-black px-6 py-3 uppercase tracking-[0.28em] text-[11px] transition-transform hover:scale-[1.02]"
                style={{ background: pillar.accent, color: "#0c0a10" }}
              >
                Got it
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Press() {
  const items = [
    {
      outlet: "TV Insider",
      headline: "Why AutoMotoVille is the network car culture has waited for",
      date: "Apr 2026",
      href: "https://example.com/press/tv-insider",
    },
    {
      outlet: "Variety",
      headline: "Inside the next-gen network betting on real American gearheads",
      date: "Mar 2026",
      href: "https://example.com/press/variety",
    },
    {
      outlet: "The Drive",
      headline: "AutoMotoVille's open casting model gives back the camera",
      date: "Feb 2026",
      href: "https://example.com/press/the-drive",
    },
  ];
  return (
    <section
      id="press"
      data-testid="press-section"
      className="relative py-28 md:py-36 px-6 bg-[#0d0d14]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <div className="font-accent text-sm text-[#FF2D9C] mb-4">
            / 05 — PRESS
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black leading-[1.05] max-w-3xl">
            What people are <span className="neon-text">saying.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((p, i) => (
            <a
              key={i}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`press-item-${i}`}
              className="group p-6 border border-white/10 bg-white/[0.02] hover:border-[#FF2D9C]/50 hover:-translate-y-1 transition-all duration-500"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-accent text-xs text-[#00E5FF] tracking-[0.22em]">
                  {p.outlet.toUpperCase()}
                </span>
                <span className="font-accent text-[10px] text-white/40 tracking-widest">
                  {p.date}
                </span>
              </div>
              <h3 className="font-heading text-xl font-black leading-snug">
                {p.headline}
              </h3>
              <div className="mt-6 flex items-center gap-2 text-[#00E5FF] font-accent text-xs">
                Read <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </a>
          ))}
        </div>
        <div className="mt-10 text-sm text-white/55">
          Press inquiries:{" "}
          <a
            href="mailto:prezbosslady@automotoville.com"
            className="text-[#00E5FF] hover:underline"
            data-testid="press-email-link"
          >
            prezbosslady@automotoville.com
          </a>
        </div>
      </div>
    </section>
  );
}


function Producers({ nav }) {
  return (
    <section
      id="producers"
      data-testid="producers-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <div className="font-accent text-sm text-[#00E5FF] mb-4">
            / 06 — FOR PRODUCERS
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black leading-[1.05] mb-6">
            You bring the show.
            <br />
            <span className="neon-text">We bring the audience.</span>
          </h2>
          <p className="text-white/70 leading-relaxed mb-4">
            Independent producers, EPs, and writer-directors with a real-world
            gearhead concept — we want to talk. AutoMotoVille is open to format
            pitches, pilots-in-a-can, and loose treatments. We co-produce, we
            license, and we partner.
          </p>
          <p className="text-white/55 text-sm leading-relaxed">
            Submit a brief logline + sizzle reel link to{" "}
            <a
              href="mailto:prezbosslady@automotoville.com"
              className="text-[#00E5FF] hover:underline"
              data-testid="producers-email-link"
            >
              prezbosslady@automotoville.com
            </a>
            , or pitch via the Submit a Story form.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              onClick={nav.openStory}
              data-testid="producers-pitch-btn"
              className="bg-[#FF2D9C] text-white font-black px-7 py-3 uppercase tracking-[0.22em] text-xs glow-pink transition-all hover:scale-[1.02] flex items-center gap-2"
            >
              <PenSquare className="w-4 h-4" /> Submit a Pitch
            </button>
            <a
              href="mailto:prezbosslady@automotoville.com"
              data-testid="producers-email-btn"
              className="font-accent text-xs tracking-[0.22em] text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/10 px-5 py-3 transition-colors"
            >
              EMAIL US
            </a>
          </div>
        </div>
        <div className="md:col-span-5 grid grid-cols-2 gap-3">
          {[
            { k: "Co-Pro", v: "Open" },
            { k: "Sizzle Reels", v: "Welcome" },
            { k: "Treatments", v: "1–3 pages" },
            { k: "Format", v: "Scripted + unscripted" },
          ].map((x) => (
            <div
              key={x.k}
              className="border border-white/10 p-5 bg-white/[0.02]"
            >
              <div className="font-heading text-xl font-black">{x.k}</div>
              <div className="font-accent text-[10px] text-white/50 tracking-widest mt-2">
                {x.v.toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function FAQs() {
  const items = [
    {
      q: "Who can apply for AutoMotoVille casting?",
      a: "Anyone 18+ in the U.S. with a real story tied to trucking, biking, cars, or boating. You don't need TV experience — you need a point of view.",
    },
    {
      q: "Do I need professional video for my intro tape?",
      a: "No. Phone video is fine. Clear audio is what matters most. We're looking for personality, not production value.",
    },
    {
      q: "How long until I hear back?",
      a: "We respond to every applicant within 14 days. If a producer wants a follow-up, we'll reach out at the email you provided.",
    },
    {
      q: "Is there a fee to apply?",
      a: "Never. AutoMotoVille casting is free, end-to-end.",
    },
    {
      q: "Can I submit on behalf of someone else?",
      a: "Yes — but get their consent first, and put their email in the application so we can confirm.",
    },
    {
      q: "What happens to my photos and video?",
      a: "They're stored privately and reviewed by our casting team only. They are not shared, sold, or used in marketing without a separate signed release.",
    },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      id="faqs"
      data-testid="faqs-section"
      className="relative py-28 md:py-36 px-6 bg-[#0d0d14]"
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <div className="font-accent text-sm text-[#FF2D9C] mb-4">
            / 07 — FAQs
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black leading-[1.05]">
            The straight <span className="neon-text">answers.</span>
          </h2>
        </div>
        <div className="border border-white/10 divide-y divide-white/10">
          {items.map((it, i) => {
            const open = openIdx === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full text-left p-5 md:p-6 flex items-start justify-between gap-4 hover:bg-white/[0.02] transition-colors"
                >
                  <span className="font-heading text-lg md:text-xl font-black pr-2">
                    {it.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#00E5FF] shrink-0 mt-1 transition-transform duration-300 ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {open && (
                  <div className="px-5 md:px-6 pb-6 -mt-1 text-white/70 leading-relaxed">
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


function Contact({ onOpenChat }) {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-accent text-sm text-[#00E5FF] mb-4">
            / 08 — CONTACT
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black leading-[1.05] mb-5">
            Drop us a <span className="neon-text">line.</span>
          </h2>
          <p className="text-white/65 leading-relaxed mb-7">
            Casting inquiries, creator submissions, business partnerships,
            sponsorship opportunities, or investor interest — reach out
            directly to the AutoMotoVille team.
          </p>

          <div className="space-y-3">
            <a
              href="mailto:casting@automotoville.com"
              data-testid="contact-email-casting"
              className="group block border border-white/10 hover:border-[#FF2D9C]/60 bg-white/[0.02] hover:bg-[#FF2D9C]/[0.05] p-4 transition-all hover:shadow-[0_8px_32px_-8px_rgba(255,45,156,0.4)]"
            >
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-10 h-10 border border-[#FF2D9C]/40 bg-[#FF2D9C]/5 flex items-center justify-center text-[#FF2D9C] group-hover:bg-[#FF2D9C]/15 transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-accent text-[10px] tracking-[0.28em] text-white/60 mb-1">
                    CASTING &amp; TALENT SUBMISSIONS
                  </div>
                  <div className="font-heading text-base font-black text-white group-hover:text-[#FF2D9C] transition-colors break-all">
                    casting@automotoville.com
                  </div>
                </div>
              </div>
            </a>

            <a
              href="mailto:prezbosslady@automotoville.com"
              data-testid="contact-email-prez"
              className="group block border border-white/10 hover:border-[#00E5FF]/60 bg-white/[0.02] hover:bg-[#00E5FF]/[0.05] p-4 transition-all hover:shadow-[0_8px_32px_-8px_rgba(0,229,255,0.4)]"
            >
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-10 h-10 border border-[#00E5FF]/40 bg-[#00E5FF]/5 flex items-center justify-center text-[#00E5FF] group-hover:bg-[#00E5FF]/15 transition-colors">
                  <Briefcase className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-accent text-[10px] tracking-[0.28em] text-white/60 mb-1">
                    BUSINESS, PARTNERSHIPS &amp; INVESTOR INQUIRIES
                  </div>
                  <div className="font-heading text-base font-black text-white group-hover:text-[#00E5FF] transition-colors break-all">
                    prezbosslady@automotoville.com
                  </div>
                </div>
              </div>
            </a>

            {/* Live Culture Chat — boxed neon support card. Clickable;
                opens the same Live Culture Chat popup as the floating
                bubble. Email links above stay pure mailto so they always
                hand off to the device's mail chooser instead of opening
                the chat. */}
            <button
              type="button"
              onClick={onOpenChat}
              data-testid="contact-live-chat-card"
              className="group relative w-full text-left border border-white/10 bg-gradient-to-br from-[#FF2D9C]/[0.06] via-white/[0.02] to-[#00E5FF]/[0.06] p-4 overflow-hidden hover:border-white/30 hover:shadow-[0_8px_36px_-10px_rgba(255,45,156,0.35),0_0_0_1px_rgba(0,229,255,0.18)_inset] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/70"
              aria-label="Open Live Culture Chat"
            >
              <div
                className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-30 pointer-events-none group-hover:opacity-50 transition-opacity"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,45,156,0.5), transparent 65%)",
                }}
              />
              <div
                className="absolute -bottom-14 -left-14 w-40 h-40 rounded-full opacity-25 pointer-events-none group-hover:opacity-45 transition-opacity"
                style={{
                  background:
                    "radial-gradient(circle, rgba(0,229,255,0.5), transparent 65%)",
                }}
              />
              <div className="relative flex items-start gap-3">
                <span className="shrink-0 w-10 h-10 border border-white/30 bg-black/30 backdrop-blur-sm flex items-center justify-center transition-colors group-hover:border-white/50">
                  <Headphones className="w-4 h-4 text-white" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-accent text-[10px] tracking-[0.28em] text-[#00E5FF] mb-1">
                    NEW · COMING SOON
                  </div>
                  <div className="font-heading text-base font-black text-white">
                    Live Culture Chat
                  </div>
                  <div className="text-xs text-white/65 mt-1 leading-relaxed">
                    Real-time creator and community support coming soon.
                  </div>
                </div>
                <ArrowRight className="shrink-0 w-4 h-4 text-white/55 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
          </div>
        </div>
        <div className="md:col-span-7">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}


function Newsletter() {
  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-24 md:py-28 px-6 bg-[#0d0d14] border-t border-b border-white/5"
    >
      <div className="max-w-5xl mx-auto text-center">
        <div className="font-accent text-sm text-[#FF2D9C] mb-4 tracking-[0.22em]">
          / 09 — NEWSLETTER
        </div>
        <h2 className="font-heading text-3xl md:text-5xl font-black leading-[1.05] mb-4">
          The <span className="neon-text">monthly dispatch.</span>
        </h2>
        <p className="text-white/70 max-w-2xl mx-auto leading-relaxed mb-8">
          New shows, new casting calls, behind-the-scenes from the road. One
          email a month, no fluff.
        </p>
        <div className="flex justify-center">
          <NewsletterForm />
        </div>
      </div>
    </section>
  );
}

function Footer({ nav }) {
  // Each link's behavior is explicit. Empty href="#" is never used.
  // - "scroll": scrolls to a section by id
  // - "action": calls a handler from `nav` (modal openers)
  const groups = [
    {
      t: "Network",
      links: [
        { label: "About", testid: "about", action: () => nav.scrollTo("about") },
        { label: "Shows", testid: "shows", action: () => nav.scrollTo("shows") },
        { label: "Culture", testid: "culture", action: () => nav.scrollTo("culture") },
        { label: "Press", testid: "press", action: () => nav.scrollTo("press") },
      ],
    },
    {
      t: "Casting",
      links: [
        { label: "Apply Now", testid: "apply-now", action: nav.openApply, accent: true },
        { label: "Submit a Story", testid: "submit-a-story", action: nav.openStory },
        { label: "FAQs", testid: "faqs", action: () => nav.scrollTo("faqs") },
        { label: "Producers", testid: "producers", action: () => nav.scrollTo("producers") },
      ],
    },
    {
      t: "Connect",
      links: [
        { label: "Contact", testid: "contact", action: () => nav.scrollTo("contact") },
        { label: "Careers", testid: "careers", action: nav.openCareers },
        { label: "Sign In", testid: "sign-in", action: nav.openSignIn },
        { label: "Newsletter", testid: "newsletter", action: () => nav.scrollTo("newsletter") },
      ],
    },
  ];

  const socialLinks = [
    {
      Icon: Instagram,
      href: "https://instagram.com/automotoville",
      label: "Instagram",
    },
    {
      Icon: Youtube,
      href: "https://youtube.com/@automotoville",
      label: "YouTube",
    },
    {
      Icon: Facebook,
      href: "https://facebook.com/automotoville",
      label: "Facebook",
    },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-white/10 px-6 pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              data-testid="footer-logo-link"
              aria-label="Back to top"
            >
              <img
                src={LOGO}
                alt="AutoMotoVille"
                className="amv-logo mb-6"
                style={{ height: "80px", width: "auto" }}
                data-testid="footer-logo"
              />
            </button>
            <p className="text-white/60 max-w-md leading-relaxed">
              AutoMotoVille — A television network built for the road, the
              track, the harbor, and everything in between.
            </p>
            <div className="flex items-center gap-4 mt-6">
              {socialLinks.map((s, i) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-link-${i}`}
                  aria-label={s.label}
                  className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#FF2D9C] hover:text-[#FF2D9C] transition-colors"
                >
                  <s.Icon className="w-4 h-4" />
                </a>
              ))}
              <a
                href="mailto:casting@automotoville.com"
                data-testid="social-link-email"
                aria-label="Email"
                className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#FF2D9C] hover:text-[#FF2D9C] transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
            {groups.map((g) => (
              <div key={g.t}>
                <div className="font-accent text-xs text-[#00E5FF] mb-4">
                  {g.t}
                </div>
                <ul className="space-y-3">
                  {g.links.map((li) => (
                    <li key={li.label}>
                      <button
                        type="button"
                        onClick={li.action}
                        data-testid={`footer-link-${li.testid}`}
                        className={`text-left text-sm transition-colors ${
                          li.accent
                            ? "text-[#FF2D9C] hover:text-white font-bold"
                            : "text-white/70 hover:text-white"
                        }`}
                      >
                        {li.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-white/40 text-xs font-accent">
            © 2026 AUTOMOTOVILLE™ · ALL RIGHTS RESERVED
          </div>
          <div className="text-white/40 text-xs font-accent">
            PRIVACY · TERMS · COOKIES
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyContext, setApplyContext] = useState(null); // {kind, title} | null
  const [activeFlow, setActiveFlow] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [comingSoon, setComingSoon] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = () => setChatOpen(true);
  const closeChat = () => setChatOpen(false);

  // Shared shows state — fetched once, passed to Featured + Shows.
  const [shows, setShows] = useState(FALLBACK_SHOWS);
  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shows`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        if (active && Array.isArray(data) && data.length > 0) setShows(data);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      active = false;
    };
  }, []);

  const setSpeaksImage =
    shows.find((s) => (s.kind || "") === "set-speaks")?.image_url || "";

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Deep-link from category destination pages: e.g. `/?apply=built-not-bought`
  // pops the right intake modal/flow when Landing mounts, then clears the
  // query string so it doesn't re-trigger on reloads.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const apply = params.get("apply");
    if (!apply) return;
    // Defer one tick so the modals/flows are mounted.
    const t = setTimeout(() => {
      handlePickLane(apply);
      window.history.replaceState({}, "", "/");
    }, 30);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const openApply = () => {
    setApplyContext(null);
    setApplyOpen(true);
  };
  const closeApply = () => setApplyOpen(false);
  const openStory = () => setStoryOpen(true);
  const closeStory = () => setStoryOpen(false);

  const openCultureFiles = () => setActiveFlow("culture-files");
  const openEvents = () => setActiveFlow("events");
  const openReenactment = () => setActiveFlow("reenactment");
  const openFood = () => setActiveFlow("food");
  const openPerformers = () => setActiveFlow("performers");
  const openContentCreators = () => setActiveFlow("content-creators");
  const openDating = () => setActiveFlow("dating");
  const closeFlow = () => setActiveFlow(null);

  const handlePickLane = (action) => {
    if (action === "love-in-the-fast-lane") return openDating();
    if (action === "built-not-bought") {
      setApplyContext({ kind: "built_not_bought", title: "Built Not Bought" });
      setApplyOpen(true);
      return;
    }
    if (action === "culture-files") return openCultureFiles();
    if (action === "events") return openEvents();
    if (action === "food") return openFood();
    if (action === "performers") return openPerformers();
    if (action === "content-creators") return openContentCreators();
    if (action === "reenactment") return openReenactment();
    if (action === "set-speaks-page") {
      window.location.href = "/the-set-speaks";
      return;
    }
    if (action === "booked-outside-page") {
      window.location.href = "/booked-outside";
      return;
    }
  };

  const openCareers = () =>
    setComingSoon({
      title: "Careers at AutoMotoVille",
      blurb:
        "We're hiring producers, editors, story leads, field crews, and post talent. Drop your email and we'll send open roles the moment our careers portal is live.",
      captureEmail: true,
      dataPrefix: "careers",
    });

  const openSignIn = () =>
    setComingSoon({
      title: "Applicant Sign In",
      blurb:
        "The applicant portal is on the way — sign in to see your application status, replace your reel, and track casting feedback. Get notified when it goes live.",
      captureEmail: true,
      dataPrefix: "signin",
    });

  const navigation = {
    scrollTo,
    openApply,
    openStory,
    openCareers,
    openSignIn,
  };

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white">
      <Header nav={navigation} />
      <Hero onApply={openApply} onCasting={() => scrollTo("shows")} />
      <Marquee />
      <Culture />
      <Featured
        onApply={() => {
          window.location.href = "/the-set-speaks";
        }}
        posterImage={setSpeaksImage}
      />
      <Shows onPickLane={handlePickLane} shows={shows} />
      <StartScreen onApply={openApply} />
      <About />
      <Press />
      <Producers nav={navigation} />
      <FAQs />
      <Contact onOpenChat={openChat} />
      <Newsletter />
      <Footer nav={navigation} />
      <ApplyModal
        open={applyOpen}
        onClose={closeApply}
        showContext={applyContext}
      />
      <MultiStepFormModal
        open={activeFlow === "culture-files"}
        onClose={closeFlow}
        config={FLOW_CONFIGS["culture-files"]}
      />
      <MultiStepFormModal
        open={activeFlow === "events"}
        onClose={closeFlow}
        config={FLOW_CONFIGS["events"]}
      />
      <MultiStepFormModal
        open={activeFlow === "reenactment"}
        onClose={closeFlow}
        config={FLOW_CONFIGS["reenactment"]}
      />
      <MultiStepFormModal
        open={activeFlow === "food"}
        onClose={closeFlow}
        config={FLOW_CONFIGS["food"]}
      />
      <MultiStepFormModal
        open={activeFlow === "performers"}
        onClose={closeFlow}
        config={FLOW_CONFIGS["performers"]}
      />
      <MultiStepFormModal
        open={activeFlow === "content-creators"}
        onClose={closeFlow}
        config={FLOW_CONFIGS["content-creators"]}
      />
      <MultiStepFormModal
        open={activeFlow === "dating"}
        onClose={closeFlow}
        config={FLOW_CONFIGS["dating"]}
      />
      <SubmitStoryModal open={storyOpen} onClose={closeStory} />
      <ComingSoonModal
        open={!!comingSoon}
        onClose={() => setComingSoon(null)}
        title={comingSoon?.title || ""}
        blurb={comingSoon?.blurb || ""}
        captureEmail={comingSoon?.captureEmail !== false}
        dataPrefix={comingSoon?.dataPrefix || "coming-soon"}
      />
      <LiveCultureChat open={chatOpen} onClose={closeChat} />
      {/* Floating Live Culture Chat bubble — fixed bottom-right.
          Lives at the root so it's available across the whole homepage. */}
      <button
        type="button"
        onClick={openChat}
        aria-label="Open Live Culture Chat"
        data-testid="live-chat-fab"
        className="group fixed z-[90] bottom-20 right-5 sm:bottom-24 sm:right-6 inline-flex items-center gap-3 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/70"
      >
        <span
          className="hidden sm:inline-flex items-center px-3 py-1.5 border border-white/15 bg-black/65 backdrop-blur-md font-accent text-[10px] tracking-[0.28em] text-white/85 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap"
          aria-hidden
        >
          LIVE CULTURE CHAT
        </span>
        <span
          className="relative w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-[#FF2D9C] to-[#00E5FF] shadow-[0_0_30px_rgba(255,45,156,0.55),0_0_60px_-10px_rgba(0,229,255,0.55)] transition-transform duration-300 group-hover:scale-105 group-active:scale-95"
        >
          <span className="absolute inset-0 rounded-full bg-[#08070a] m-[2px]" />
          <MessageCircle className="relative w-6 h-6 text-white" />
          <span
            className="absolute inset-0 rounded-full ring-2 ring-[#FF2D9C]/50 animate-ping"
            aria-hidden
          />
        </span>
      </button>
    </div>
  );
}

// ============================================================
// FLOATING LIVE CULTURE CHAT — homepage support entrypoint.
// Real in-app concierge contact form. Submits to the dynamic
// intake pipeline (POST /api/intake/contact) which fires
// applicant + admin emails via Resend. No mailto.
// ============================================================
const CHAT_SUBJECTS = [
  "Casting",
  "Travel Agent Questions",
  "Sponsorships",
  "Creator Opportunities",
  "Group Travel",
  "General Support",
];

function LiveCultureChat({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: CHAT_SUBJECTS[0],
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // Reset state every time the modal opens so a returning user
  // sees a clean form instead of the previous success state.
  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        email: "",
        subject: CHAT_SUBJECTS[0],
        message: "",
      });
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name || !email || !message) {
      setErrorMsg("Name, email, and message are all required.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/intake/contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            full_name: name,
            email,
            data: {
              subject: form.subject,
              message,
              source: "live-culture-chat",
            },
            tags: ["contact", form.subject.toLowerCase().replace(/\s+/g, "-")],
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("success");
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again in a moment.");
      setStatus("error");
    }
  };

  const sending = status === "sending";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:justify-end px-4 pb-4 pt-4 sm:pb-6 sm:pr-6 sm:pt-6"
          data-testid="live-chat-modal"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="live-chat-title"
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-[#0c0a10]/95 backdrop-blur-xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(255,45,156,0.45),0_0_0_1px_rgba(0,229,255,0.18)_inset]"
          >
            {/* Neon corner glows */}
            <div
              className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-40 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,45,156,0.55), transparent 65%)",
              }}
            />
            <div
              className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full opacity-30 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,229,255,0.55), transparent 65%)",
              }}
            />

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="live-chat-close"
              className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative p-6 sm:p-7">
              {/* Header */}
              <div className="flex items-center gap-3 mb-1">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2D9C] to-[#00E5FF] flex items-center justify-center shadow-[0_0_24px_rgba(255,45,156,0.4)]">
                  <Headphones className="w-4 h-4 text-white" />
                </span>
                <div className="font-accent text-[10px] tracking-[0.32em] text-[#00E5FF]">
                  AUTOMOTOVILLE · CONCIERGE
                </div>
              </div>
              <h3
                id="live-chat-title"
                className="font-heading uppercase font-black text-2xl sm:text-3xl mt-2 leading-tight"
                style={{ color: "#F6EBD4" }}
              >
                Live Culture Chat
              </h3>

              {status === "success" ? (
                <SuccessPanel onClose={onClose} />
              ) : (
                <>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">
                    Questions about casting, travel, sponsorships, creator
                    opportunities, or joining the culture? Send us a message.
                  </p>

                  <form onSubmit={handleSubmit} className="mt-5 space-y-3">
                    <ChatField
                      id="lcc-name"
                      label="Name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      autoComplete="name"
                      testid="live-chat-name"
                      disabled={sending}
                    />
                    <ChatField
                      id="lcc-email"
                      type="email"
                      label="Email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      autoComplete="email"
                      testid="live-chat-email"
                      disabled={sending}
                    />

                    <div>
                      <label
                        htmlFor="lcc-subject"
                        className="block font-accent text-[10px] tracking-[0.28em] text-white/55 mb-1.5"
                      >
                        Subject
                      </label>
                      <div className="relative">
                        <select
                          id="lcc-subject"
                          value={form.subject}
                          onChange={update("subject")}
                          disabled={sending}
                          data-testid="live-chat-subject"
                          className="w-full appearance-none bg-black/40 border border-white/15 px-3 py-2.5 pr-9 text-sm text-white focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 transition-colors"
                        >
                          {CHAT_SUBJECTS.map((s) => (
                            <option key={s} value={s} className="bg-[#0c0a10]">
                              {s}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          className="w-4 h-4 text-white/50 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                          aria-hidden
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="lcc-message"
                        className="block font-accent text-[10px] tracking-[0.28em] text-white/55 mb-1.5"
                      >
                        Message
                      </label>
                      <textarea
                        id="lcc-message"
                        value={form.message}
                        onChange={update("message")}
                        disabled={sending}
                        rows={4}
                        maxLength={2000}
                        placeholder="Tell us what's on your mind…"
                        data-testid="live-chat-message"
                        className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF2D9C] focus:ring-1 focus:ring-[#FF2D9C]/40 transition-colors resize-none"
                      />
                    </div>

                    {status === "error" && errorMsg && (
                      <div
                        role="alert"
                        data-testid="live-chat-error"
                        className="text-xs text-[#FF6FA8] border border-[#FF2D9C]/40 bg-[#FF2D9C]/[0.06] px-3 py-2"
                      >
                        {errorMsg}
                      </div>
                    )}

                    <div className="pt-1 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={sending}
                        data-testid="live-chat-close-btn"
                        className="sm:flex-1 inline-flex items-center justify-center gap-2 border border-white/15 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/30 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3 font-heading font-black text-xs tracking-[0.18em] uppercase text-white/80 transition-colors"
                      >
                        Close Chat
                      </button>
                      <button
                        type="submit"
                        disabled={sending}
                        data-testid="live-chat-submit"
                        className="sm:flex-[1.4] group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF] hover:from-[#ff45a8] hover:to-[#33ecff] disabled:opacity-70 disabled:cursor-not-allowed px-4 py-3 font-heading font-black text-xs tracking-[0.18em] uppercase text-white shadow-[0_10px_30px_-12px_rgba(255,45,156,0.7)] transition-all"
                      >
                        {sending ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                            Send Message
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  <div className="mt-5 text-[10px] font-accent tracking-[0.28em] text-white/35 text-center">
                    REAL HUMANS · TYPICAL REPLY WITHIN 24H
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ChatField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  testid,
  disabled,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-accent text-[10px] tracking-[0.28em] text-white/55 mb-1.5"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        data-testid={testid}
        className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 transition-colors disabled:opacity-60"
      />
    </div>
  );
}

function SuccessPanel({ onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="mt-6 text-center"
      data-testid="live-chat-success"
    >
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 0.05,
          duration: 0.5,
          type: "spring",
          stiffness: 220,
          damping: 16,
        }}
        className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-[#FF2D9C] to-[#00E5FF] shadow-[0_0_40px_rgba(255,45,156,0.5),0_0_60px_-10px_rgba(0,229,255,0.5)]"
      >
        <CheckCircle2 className="w-8 h-8 text-white" />
      </motion.div>
      <motion.h4
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18, duration: 0.35 }}
        className="font-heading font-black uppercase text-xl sm:text-2xl mt-5"
        style={{ color: "#F6EBD4" }}
      >
        Message Received
      </motion.h4>
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.26, duration: 0.35 }}
        className="mt-2 text-sm text-white/70 leading-relaxed"
      >
        Thanks — a producer on our team is on it. We'll be in touch by email
        soon.
      </motion.p>
      <motion.button
        type="button"
        onClick={onClose}
        data-testid="live-chat-success-close"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.34, duration: 0.35 }}
        className="mt-6 inline-flex items-center justify-center gap-2 border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/30 px-5 py-2.5 font-heading font-black text-xs tracking-[0.18em] uppercase text-white/85 transition-colors"
      >
        Close
      </motion.button>
    </motion.div>
  );
}
